// components/marketplace/FilterPanel.tsx
"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect } from "react";

interface FilterPanelProps {
  onFilterChange: (filters: {
    providers: string[];
    popular: string[];
    reviews: string[];
  }) => void;
  onReset: () => void;
  activeFilters: {
    providers: string[];
    popular: string[];
    reviews: string[];
  };
}

export default function FilterPanel({
  onFilterChange,
  onReset,
  activeFilters,
}: FilterPanelProps) {
  const [selectedFilters, setSelectedFilters] = useState(activeFilters);

  const [openSections, setOpenSections] = useState({
    providers: true,
    popular: true,
    reviews: true,
  });

  // Sync with parent activeFilters
  useEffect(() => {
    setSelectedFilters(activeFilters);
  }, [activeFilters]);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFilterChange = (
    filterType: keyof typeof selectedFilters,
    value: string
  ) => {
    setSelectedFilters((prev) => {
      const currentValues = [...prev[filterType]];
      const index = currentValues.indexOf(value);

      const updated = {
        ...prev,
        [filterType]:
          index > -1
            ? currentValues.filter((item) => item !== value)
            : [...currentValues, value],
      };

      // Call parent's onFilterChange
      onFilterChange(updated);
      return updated;
    });
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedFilters({
      providers: [],
      popular: [],
      reviews: [],
    });
    onReset();
  };

  const providerOptions = ["Manufacturers", "Distributors", "Admin"];
  const popularOptions = ["Most Popular", "Most Quoted", "Most Sold", "Newest"];
  const reviewOptions = ["4+ stars", "3+ stars", "2+ stars"];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      {/* Filter Title */}
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Filter</h2>

      {/* Providers Filter */}
      <div className="mb-6">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            toggleSection("providers");
          }}
          className="flex justify-between w-full font-medium text-gray-700 mb-3 items-center"
        >
          <span>Providers</span>
          {openSections.providers ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {openSections.providers && (
          <div className="space-y-2">
            {providerOptions.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedFilters.providers.includes(option)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFilterChange("providers", option);
                  }}
                  className="mr-2 h-4 w-4 text-[#002301] rounded cursor-pointer"
                />
                <span className="text-sm text-gray-600">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <hr className="border-gray-200 my-4" />

      {/* Popular Filter */}
      <div className="mb-6">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            toggleSection("popular");
          }}
          className="flex justify-between w-full font-medium text-gray-700 mb-3 items-center"
        >
          <span>Most Popular</span>
          {openSections.popular ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {openSections.popular && (
          <div className="space-y-2">
            {popularOptions.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="popular" // 👈 makes them behave like radio buttons
                  checked={selectedFilters.popular[0] === option}
                  onChange={(e) => {
                    e.stopPropagation();
                    const updated = {
                      ...selectedFilters,
                      popular:
                        selectedFilters.popular[0] === option ? [] : [option],
                    };
                    setSelectedFilters(updated);
                    onFilterChange(updated);
                  }}
                  className="mr-2 h-4 w-4 text-[#002301] rounded-full cursor-pointer"
                />
                <span className="text-sm text-gray-600">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <hr className="border-gray-200 my-4" />

      {/* Reviews Filter */}
      <div className="mb-6">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            toggleSection("reviews");
          }}
          className="flex justify-between w-full font-medium text-gray-700 mb-3 items-center"
        >
          <span>Reviews</span>
          {openSections.reviews ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {openSections.reviews && (
          <div className="space-y-2">
            {reviewOptions.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedFilters.reviews.includes(option)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFilterChange("reviews", option);
                  }}
                  className="mr-2 h-4 w-4 text-[#002301] rounded cursor-pointer"
                />
                <span className="text-sm text-gray-600">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          type="button"
          onClick={handleReset}
          className="flex-1 bg-transparent text-gray-700 border border-gray-300 px-4 py-2 rounded-full hover:bg-gray-50 transition-colors duration-200 cursor-pointer hover:scale-105"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
